import { useState } from "react";
import { Trash2, X } from "lucide-react";
import { S, MANROPE, FOCUS } from "./lib";

const SCOPES = [
  ["draft", "Just this draft", "Wrong angle. The reason is kept so the next one is different."],
  ["snooze", "Leave them alone for a while", "They drop out of the queue, then come back."],
  ["never", "Never email this prospect", "A standing do-not-email until someone lifts it."],
];
const DAYS = [30, 60, 90];

// Binning a draft is only useful if the reason survives it.
export default function DiscardDialog({ email, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState("draft");
  const [days, setDays] = useState(60);

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="discard-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,.5)" }} onClick={onClose} />
      <div className="relative w-full max-w-[520px] rounded-[13px] overflow-hidden"
        style={{ background: S.card, border: `1px solid ${S.border}` }}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ borderBottom: `1px solid ${S.divider}` }}>
          <Trash2 className="w-4 h-4" style={{ color: S.bad }} />
          <div className="text-[14px] font-bold" style={{ ...MANROPE, color: S.ink }}>
            Bin {email.ref}
          </div>
          <button onClick={onClose} data-testid="discard-close"
            className={`ml-auto h-8 w-8 rounded-[6px] grid place-items-center ${FOCUS}`}
            style={{ background: S.panel, border: `1px solid ${S.border}` }}>
            <X className="w-4 h-4" style={{ color: S.ink2 }} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] mb-1" style={{ color: S.ink3 }}>
              Why
            </div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              autoFocus data-testid="discard-reason"
              placeholder="They went with someone else. Wrong contact. Too soon after the last one."
              className={`w-full px-2.5 py-2 rounded-[7px] text-[13px] ${FOCUS}`}
              style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink }} />
            <div className="text-[10.5px] mt-1" style={{ color: S.muted }}>
              Claude reads this before it drafts to them again, so write it for the next attempt.
            </div>
          </div>

          <div className="space-y-1.5">
            {SCOPES.map(([key, label, hint]) => (
              <button key={key} onClick={() => setScope(key)} data-testid={`discard-scope-${key}`}
                className={`w-full text-left px-3 py-2 rounded-[8px] ${FOCUS}`}
                style={{ background: scope === key ? "#FFFBEB" : S.panel,
                  border: `1px solid ${scope === key ? S.accentEdge : S.border}` }}>
                <div className="text-[12.5px] font-semibold" style={{ color: S.ink }}>{label}</div>
                <div className="text-[11px]" style={{ color: S.ink3 }}>{hint}</div>
              </button>
            ))}
          </div>

          {scope === "snooze" && (
            <div className="flex items-center gap-2">
              <div className="text-[11.5px]" style={{ color: S.ink2 }}>Leave them for</div>
              {DAYS.map((d) => (
                <button key={d} onClick={() => setDays(d)} data-testid={`discard-days-${d}`}
                  className={`h-8 px-2.5 rounded-[6px] text-[11.5px] font-bold ${FOCUS}`}
                  style={{ background: days === d ? S.accent : S.panel,
                    border: `1px solid ${days === d ? S.accentEdge : S.border}`,
                    color: days === d ? "#000" : S.ink2 }}>
                  {d} days
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button onClick={() => onConfirm({ reason, scope, days })}
              disabled={busy || !reason.trim()} data-testid="discard-confirm"
              className={`h-9 px-3.5 rounded-[7px] text-[11.5px] font-bold uppercase tracking-[0.04em] disabled:opacity-50 ${FOCUS}`}
              style={{ background: S.bad, border: `1px solid ${S.bad}`, color: "#fff", ...MANROPE }}>
              Bin it
            </button>
            <button onClick={onClose} data-testid="discard-cancel"
              className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
              style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2, ...MANROPE }}>
              Keep it
            </button>
            {!reason.trim() && (
              <span className="text-[11px]" style={{ color: S.muted }}>A reason is required</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
